import { useState, useEffect, useCallback, useRef, KeyboardEvent } from 'react'

interface SpellingInputProps {
  onSubmit: (letters: string) => void
  disabled?: boolean
  autoFocus?: boolean
}

export default function SpellingInput({ onSubmit, disabled, autoFocus }: SpellingInputProps) {
  const [letters, setLetters] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (autoFocus && !disabled && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus, disabled])

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const scrollIntoView = () => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }

    vv.addEventListener('resize', scrollIntoView)
    return () => vv.removeEventListener('resize', scrollIntoView)
  }, [])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    if (e.key === 'Enter' && letters.length > 0) {
      e.preventDefault()
      onSubmit(letters.join(''))
      return
    }
    if (e.key === 'Backspace') {
      e.preventDefault()
      setLetters(prev => prev.slice(0, -1))
      return
    }
    if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
      e.preventDefault()
      setLetters(prev => [...prev, e.key.toLowerCase()])
    }
  }, [disabled, letters, onSubmit])

  const handleClear = () => {
    setLetters([])
    inputRef.current?.focus()
  }

  const handleBackspace = () => {
    setLetters(prev => prev.slice(0, -1))
    inputRef.current?.focus()
  }

  const handleSubmit = () => {
    if (letters.length > 0) {
      onSubmit(letters.join(''))
    }
  }

  return (
    <div ref={containerRef} className="relative space-y-4">
      {/* Letter tiles */}
      <div
        className="flex items-center justify-center gap-1.5 min-h-[56px] flex-wrap cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {letters.length === 0 ? (
          <span className="text-muted-foreground text-lg">Type the letters...</span>
        ) : (
          letters.map((l, i) => (
            <div
              key={i}
              className="w-10 h-12 flex items-center justify-center border-2 border-primary/30 rounded-lg bg-background text-xl font-bold uppercase"
            >
              {l}
            </div>
          ))
        )}
        <div className="w-10 h-12 flex items-center justify-center border-2 border-dashed border-muted-foreground/30 rounded-lg animate-pulse">
          <span className="text-muted-foreground">_</span>
        </div>
      </div>

      {/* Input for keyboard capture — positioned inline but invisible so mobile browsers scroll to it */}
      <input
        ref={inputRef}
        type="text"
        className="opacity-0 absolute h-0 w-0 pointer-events-none"
        style={{ fontSize: '16px' }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label="Spell the word"
      />

      {/* Action buttons */}
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled || letters.length === 0}
          className="px-4 py-3 border border-border rounded-xl text-muted-foreground hover:bg-accent transition disabled:opacity-30 min-h-[44px] min-w-[44px]"
          title="Clear all"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleBackspace}
          disabled={disabled || letters.length === 0}
          className="px-4 py-3 border border-border rounded-xl text-muted-foreground hover:bg-accent transition disabled:opacity-30 min-h-[44px] min-w-[44px]"
          title="Delete last letter"
        >
          &larr;
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || letters.length === 0}
          className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition disabled:opacity-30 min-h-[44px]"
        >
          Check
        </button>
      </div>
    </div>
  )
}
